/**
 * Collection schemas
 * Moved from constants.ts for better organization
 */

import { z } from 'zod';
import { COLLECTION_DISPLAY, COLLECTION_MODEL } from '../../constants';

// ============================================================================
// Collection Item Schema
// ============================================================================

/**
 * Collection item schema - represents cards, dashboards, etc. in collections
 */
export const collectionItemSchema = z.object({
  archived: z.boolean(),
  can_delete: z.boolean(),
  can_restore: z.boolean(),
  can_write: z.boolean(),
  collection_id: z.number().nullable(),
  dashboard_id: z.number().nullable(),
  database_id: z.number().nullable(),
  description: z.string().nullable(),
  effective_location: z.string().optional(),
  entity_id: z.string().nullable(),
  id: z.number(),
  last_used_at: z.string().nullable(),
  'last-edit-info': z
    .object({
      id: z.number(),
      last_name: z.string().nullable(),
      first_name: z.string(),
      email: z.string(),
      timestamp: z.string(),
    })
    .optional(),
  location: z.string().nullable(),
  model: z.enum(COLLECTION_MODEL),
  name: z.string(),
  display: z.enum(COLLECTION_DISPLAY).optional(),
  fully_parameterized: z.boolean().optional(),
});

export const collectionItemsSchema = z.array(collectionItemSchema);

export type CollectionItem = z.infer<typeof collectionItemSchema>;

/**
 * Transformed collection item with string ID
 * Used in UI to handle non-unique numeric IDs
 */
export type TransformedCollectionItem = Omit<CollectionItem, 'id'> & {
  id: string;
  originId: number;
};

// ============================================================================
// Collection Schema
// ============================================================================

const effectiveAncestorSchema = z.object({
  can_write: z.boolean(),
  id: z.number(),
  name: z.string(),
  personal_owner_id: z.number().nullable(),
  type: z.string().nullable(),
});

export type EffectiveAncestor = z.infer<typeof effectiveAncestorSchema>;

const collectionSchemaBase = z.object({
  id: z.union([z.number(), z.string()]),
  authority_level: z.string().nullable(),
  can_write: z.boolean(),
  name: z.string(),
  is_personal: z.boolean(),
  can_delete: z.boolean(),
});

const collectionSchemaOptional = z
  .object({
    effective_ancestors: z.array(effectiveAncestorSchema),
    can_restore: z.boolean(),
    effective_location: z.string().nullable(),
    parent_id: z.number().nullable(),
    description: z.string().nullable(),
    archived: z.boolean(),
    slug: z.string(),
    archive_operation_id: z.string().nullable(),
    personal_owner_id: z.number().nullable(),
    type: z.string().nullable(),
    is_sample: z.boolean(),
    archived_directly: z.string().nullable(),
    entity_id: z.string(),
    location: z.string(),
    namespace: z.string().nullable(),
    created_at: z.string(),
  })
  .partial();

export const collectionSchema = collectionSchemaBase.merge(
  collectionSchemaOptional
);

export const collectionsSchema = z.array(collectionSchema);

export type Collection = z.infer<typeof collectionSchema>;
